import type { JSX } from 'react';

import type { PrefetchedDataProps, PrefetchedUserDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import type {
	PetitionDetailsPagePrefetchedContext,
	PetitionDetailsPagePrefetchedUserContext,
} from 'src/app/pages/petitionDetails/pageContext';
import { prefetchPageContext, prefetchPageUserContext } from 'src/app/pages/petitionDetails/pageContext';
import type { PetitionHeadlineBanditData, Sharer } from 'src/app/pages/petitionDetails/shared/types';
import { headlineReplacements } from 'src/app/pages/petitionDetails/shared/utilities';
import { isLoaded } from 'src/app/shared/utils/async';

import { getCopylinkBanditVariantText, getSharer, getWhatsappBanditVariantText } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';
import {
	buildMetaContext,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from '../shared';
import type { PetitionDetailsMetaContext } from '../shared';

type PrefetchedData = PetitionDetailsPagePrefetchedContext &
	Readonly<{
		metaContext: PetitionDetailsMetaContext;
		sharer?: Sharer;
		variant?: PetitionHeadlineBanditData;
		bestShareVisual: boolean;
	}>;

export const PetitionDetailsBandit = withPrefetchedData(
	({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> &
		PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>): JSX.Element => {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: { slug, shareType, experimentVariantName, sharerUserId },
			} = context;

			if (!(slug && experimentVariantName && sharerUserId)) return {} as PrefetchedData;
			if (!(shareType && ['wa', 'cl_'].includes(shareType))) return {} as PrefetchedData;

			const [pageContext, variant, sharer] = await Promise.all([
				prefetchPageContext(slug, context),
				(shareType === 'wa'
					? getWhatsappBanditVariantText(slug, experimentVariantName, context.utilityContext)
					: getCopylinkBanditVariantText(slug, experimentVariantName, context.utilityContext)
				).catch(() => undefined),
				getSharer(sharerUserId, context.utilityContext).catch(() => undefined),
			]);

			return {
				...pageContext,
				metaContext: buildMetaContext(context),
				variant,
				sharer,
				bestShareVisual: context.path.endsWith('/bsv'),
			};
		},
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const shareHeadlineOverrides = (() => {
				const replacements = headlineReplacements({
					pageData: prefetchedData.pageData.pageData,
					sharer: prefetchedData.sharer,
					i18n,
				});
				if (prefetchedData.variant) {
					return {
						title: i18n.replacePlaceholders(prefetchedData.variant.headline, replacements),
						description: i18n.replacePlaceholders(prefetchedData.variant.description, replacements),
					};
				}
				if (prefetchedData.sharer) {
					return {
						title: i18n.translate('fe.components.petition_share.share_text_fb', replacements),
						description: replacements.petition_name as string,
					};
				}
				return undefined;
			})();
			return petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				getStaticAssetUrl,
				metaContext: prefetchedData.metaContext,
				bestShareVisual: prefetchedData.bestShareVisual,
				shareHeadlineOverrides,
			});
		},
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
	},
);
